import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Database,
  Users,
  ShoppingBag,
  Truck,
  Wheat,
  Syringe,
  Scale,
  Plus,
  Search,
  Edit2,
  CheckCircle2,
  XCircle,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Archive,
  RotateCcw,
  SlidersHorizontal,
  FileText,
  Tag,
  LucideIcon,
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingState } from '@/components/common/LoadingState';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency, formatDate } from '@/lib/utils';
import * as XLSX from 'xlsx';

export type MasterEntityKey = 'growers' | 'buyers' | 'suppliers' | 'feed_products' | 'medicine_products' | 'rate_rules';

export interface EntityConfig {
  id: MasterEntityKey;
  name: string;
  table: string;
  icon: LucideIcon;
  codeField: string;
  nameField: string;
  itemNoun: string;
}

export const ENTITY_CONFIGS: EntityConfig[] = [
  { id: 'growers', name: 'Growers (Raisers)', table: 'growers', icon: Users, codeField: 'grower_code', nameField: 'full_name', itemNoun: 'Grower' },
  { id: 'buyers', name: 'Customers (Buyers)', table: 'buyers', icon: ShoppingBag, codeField: 'buyer_code', nameField: 'buyer_name', itemNoun: 'Buyer' },
  { id: 'suppliers', name: 'Suppliers', table: 'suppliers', icon: Truck, codeField: 'supplier_code', nameField: 'supplier_name', itemNoun: 'Supplier' },
  { id: 'feed_products', name: 'Feed Products', table: 'feed_products', icon: Wheat, codeField: 'product_code', nameField: 'product_name', itemNoun: 'Feed Product' },
  { id: 'medicine_products', name: 'Medicine Products', table: 'medicine_products', icon: Syringe, codeField: 'product_code', nameField: 'product_name', itemNoun: 'Medicine' },
  { id: 'rate_rules', name: 'Contract Rate Rules', table: 'rate_rules', icon: Scale, codeField: 'rule_code', nameField: 'rule_name', itemNoun: 'Rate Rule' },
];

export const MasterDataPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('master_tables', 'edit');

  const [activeEntity, setActiveEntity] = useState<MasterEntityKey>('growers');
  const [items, setItems] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');

  // Modals & Menus
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  const currentConfig = useMemo(() => {
    return ENTITY_CONFIGS.find((e) => e.id === activeEntity) || ENTITY_CONFIGS[0];
  }, [activeEntity]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from(currentConfig.table)
        .select('*')
        .order('is_active', { ascending: false });

      if (!error && data) {
        setItems(data);
      } else {
        setItems([]);
      }

      // Preload suppliers for dropdowns if needed
      if (activeEntity === 'feed_products' || activeEntity === 'medicine_products') {
        const { data: supData } = await supabase.from('suppliers').select('id, supplier_name').eq('is_active', true);
        if (supData) setSuppliers(supData);
      }
    } catch (err) {
      console.error('Error loading master data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeEntity, currentConfig]);

  useEffect(() => {
    loadData();
    setSearchQuery('');
    setStatusFilter('all');
  }, [activeEntity, loadData]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (statusFilter === 'active' && item.is_active === false) return false;
      if (statusFilter === 'archived' && item.is_active !== false) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const code = String(item[currentConfig.codeField] || '').toLowerCase();
        const name = String(item[currentConfig.nameField] || '').toLowerCase();
        const notes = String(item.notes || '').toLowerCase();
        const contact = String(item.contact_number || item.contact_person || '').toLowerCase();
        const category = String(item.category || item.supplier_type || item.rate_type || '').toLowerCase();

        return code.includes(q) || name.includes(q) || notes.includes(q) || contact.includes(q) || category.includes(q);
      }

      return true;
    });
  }, [items, searchQuery, statusFilter, currentConfig]);

  // Dynamic KPI calculations for active entity category
  const kpis = useMemo(() => {
    const total = items.length;
    const active = items.filter((i) => i.is_active !== false).length;
    const archived = items.filter((i) => i.is_active === false).length;

    return {
      total,
      active,
      archived,
    };
  }, [items]);

  // Form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Open Create Modal
  const openCreateModal = () => {
    setFormData({ is_active: true });
    setFormError(null);
    setIsCreateOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (item: any) => {
    setSelectedItem(item);
    setFormData({ ...item });
    setFormError(null);
    setIsEditOpen(true);
  };

  // Save New Record
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    try {
      const { error } = await supabase
        .from(currentConfig.table)
        .insert([formData])
        .select();

      if (error) throw error;

      setIsCreateOpen(false);
      await loadData();
    } catch (err: any) {
      setFormError(err.message || 'Failed to create record.');
    } finally {
      setSaving(false);
    }
  };

  // Save Edit Record
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    setSaving(true);
    setFormError(null);

    try {
      const { error } = await supabase
        .from(currentConfig.table)
        .update(formData)
        .eq('id', selectedItem.id);

      if (error) throw error;

      setIsEditOpen(false);
      setSelectedItem(null);
      await loadData();
    } catch (err: any) {
      setFormError(err.message || 'Failed to update record.');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Active Status
  const handleToggleStatus = async (item: any) => {
    try {
      const newStatus = !item.is_active;
      const { error } = await supabase
        .from(currentConfig.table)
        .update({ is_active: newStatus })
        .eq('id', item.id);

      if (error) throw error;
      await loadData();
    } catch (err: any) {
      alert(`Failed to change status: ${err.message}`);
    }
  };

  // Export handlers
  const handleExportCSV = () => {
    if (filteredItems.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(filteredItems);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `VSB_${activeEntity}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleExportExcel = () => {
    if (filteredItems.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(filteredItems);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, activeEntity);
    XLSX.writeFile(workbook, `VSB_${activeEntity}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  const isFiltered = searchQuery !== '' || statusFilter !== 'all';

  // Dynamic Form Field Renderer (Shared clean layout for Add and Edit)
  const renderFormFields = () => {
    switch (activeEntity) {
      case 'growers':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Full Name (Last, First) *</label>
              <Input
                required
                placeholder="e.g. Dela Cruz, Juan"
                value={formData.full_name || ''}
                onChange={(e) => handleFieldChange('full_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Contact Number</label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="0917-000-0000"
                  value={formData.contact_number || ''}
                  onChange={(e) => handleFieldChange('contact_number', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Email Address</label>
                <Input
                  type="email"
                  inputMode="email"
                  placeholder="grower@farm.com"
                  value={formData.email || ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Farm Location / Address</label>
              <Input
                placeholder="Barangay, Municipality"
                value={formData.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
          </>
        );

      case 'buyers':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Buyer / Customer Name *</label>
              <Input
                required
                placeholder="e.g. San Jose Meat Market"
                value={formData.buyer_name || ''}
                onChange={(e) => handleFieldChange('buyer_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Contact Person</label>
                <Input
                  placeholder="Contact Name"
                  value={formData.contact_person || ''}
                  onChange={(e) => handleFieldChange('contact_person', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Phone Number</label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="0917-000-0000"
                  value={formData.contact_number || ''}
                  onChange={(e) => handleFieldChange('contact_number', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
            </div>
          </>
        );

      case 'suppliers':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Supplier Company Name *</label>
              <Input
                required
                placeholder="e.g. SIDC Feeds Corp"
                value={formData.supplier_name || ''}
                onChange={(e) => handleFieldChange('supplier_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Supplier Type</label>
                <select
                  value={formData.supplier_type || 'feed'}
                  onChange={(e) => handleFieldChange('supplier_type', e.target.value)}
                  className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="feed">Feed Mill</option>
                  <option value="piglets">Piglet Breeder</option>
                  <option value="medicine">Veterinary & Meds</option>
                  <option value="hauling">Hauling & Logistics</option>
                  <option value="other">General Supplier</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Contact Person</label>
                <Input
                  placeholder="Contact Name"
                  value={formData.contact_person || ''}
                  onChange={(e) => handleFieldChange('contact_person', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
            </div>
          </>
        );

      case 'feed_products':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Feed Product Name *</label>
              <Input
                required
                placeholder="e.g. Pre-Starter Pellet"
                value={formData.product_name || ''}
                onChange={(e) => handleFieldChange('product_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Category / Stage</label>
                <Input
                  placeholder="e.g. Starter, Grower, Finisher"
                  value={formData.category || ''}
                  onChange={(e) => handleFieldChange('category', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Default Unit Price (₱/Bag)</label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="1850.00"
                  value={formData.default_unit_price ?? ''}
                  onChange={(e) => handleFieldChange('default_unit_price', e.target.value ? Number(e.target.value) : null)}
                  className="h-10 min-h-[44px] text-xs font-mono"
                />
              </div>
            </div>
          </>
        );

      case 'medicine_products':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Medicine Product Name *</label>
              <Input
                required
                placeholder="e.g. Amoxicillin 100ml"
                value={formData.product_name || ''}
                onChange={(e) => handleFieldChange('product_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Unit of Measurement</label>
                <Input
                  placeholder="e.g. Vial, Bottle, Sachet, Dose"
                  value={formData.unit_measurement || ''}
                  onChange={(e) => handleFieldChange('unit_measurement', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Default Unit Price (₱)</label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="450.00"
                  value={formData.default_unit_price ?? ''}
                  onChange={(e) => handleFieldChange('default_unit_price', e.target.value ? Number(e.target.value) : null)}
                  className="h-10 min-h-[44px] text-xs font-mono"
                />
              </div>
            </div>
          </>
        );

      case 'rate_rules':
        return (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Rule Name *</label>
              <Input
                required
                placeholder="e.g. 70/30 Profit Sharing Split"
                value={formData.rule_name || ''}
                onChange={(e) => handleFieldChange('rule_name', e.target.value)}
                className="h-10 min-h-[44px] text-xs"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Rate Type</label>
                <Input
                  placeholder="e.g. grower_share_pct, markup_rate"
                  value={formData.rate_type || ''}
                  onChange={(e) => handleFieldChange('rate_type', e.target.value)}
                  className="h-10 min-h-[44px] text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Rate Amount / %</label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.0001"
                  placeholder="0.70"
                  value={formData.rate_amount ?? ''}
                  onChange={(e) => handleFieldChange('rate_amount', e.target.value ? Number(e.target.value) : null)}
                  className="h-10 min-h-[44px] text-xs font-mono"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">Formula Expression</label>
              <Input
                placeholder="e.g. gross_profit * 0.70"
                value={formData.formula_expression || ''}
                onChange={(e) => handleFieldChange('formula_expression', e.target.value)}
                className="h-10 min-h-[44px] text-xs font-mono"
              />
            </div>
          </>
        );
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Standard PageHeader with Primary Action */}
      <PageHeader
        title="Master Data Management"
        subtitle="Canonical reference entities, growers, buyers, suppliers, feed/med catalog, and contract rate rules."
        icon={Database}
        helpScreenKey="master_data"
        actions={
          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
            <ActionTooltip content="Reload master catalog records and active counts.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                disabled={loading}
                className="text-xs h-10 min-h-[44px] px-2.5 sm:px-3 border-slate-300 hover:bg-slate-50 gap-1.5"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {/* Desktop CSV & Excel Actions */}
            <div className="hidden sm:flex items-center gap-2">
              <ActionTooltip content="Download current master data entity records to a CSV spreadsheet.">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={filteredItems.length === 0}
                  className="text-xs h-10 min-h-[44px] border-slate-300 hover:bg-slate-50 gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>CSV</span>
                </Button>
              </ActionTooltip>

              <ActionTooltip content="Download current master data entity records to formatted Excel (.xlsx).">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportExcel}
                  disabled={filteredItems.length === 0}
                  className="text-xs h-10 min-h-[44px] border-emerald-300 hover:bg-emerald-50 text-emerald-800 gap-1.5"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />
                  <span>Excel</span>
                </Button>
              </ActionTooltip>
            </div>

            {/* Compact Mobile Export Dropdown */}
            <div className="sm:hidden relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExportOpen(!isExportOpen)}
                disabled={filteredItems.length === 0}
                className="text-xs h-10 min-h-[44px] px-3 border-slate-300 hover:bg-slate-50 gap-1.5"
              >
                <Download className="w-3.5 h-3.5 text-slate-600" />
                <span>Export</span>
              </Button>
              {isExportOpen && (
                <div className="absolute right-0 top-12 z-30 bg-white border border-slate-200 rounded-xl shadow-lg p-1.5 min-w-[140px] space-y-1">
                  <button
                    onClick={() => {
                      handleExportCSV();
                      setIsExportOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-lg flex items-center gap-2 min-h-[40px]"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Export CSV</span>
                  </button>
                  <button
                    onClick={() => {
                      handleExportExcel();
                      setIsExportOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 rounded-lg flex items-center gap-2 min-h-[40px]"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-700" />
                    <span>Export Excel</span>
                  </button>
                </div>
              )}
            </div>

            {/* Clear Primary Action Button */}
            <PermissionGuard module="master_tables" action="edit">
              <ActionTooltip content={`Create a new ${currentConfig.itemNoun.toLowerCase()} record in the database.`}>
                <Button
                  size="sm"
                  onClick={openCreateModal}
                  className="text-xs font-bold bg-emerald-800 hover:bg-emerald-900 text-white h-10 min-h-[44px] px-3.5 gap-1.5 shadow-xs flex-1 sm:flex-none"
                >
                  <Plus className="w-4 h-4 text-emerald-200" />
                  <span>Add {currentConfig.itemNoun}</span>
                </Button>
              </ActionTooltip>
            </PermissionGuard>
          </div>
        }
      />

      {/* 2. Desktop Standard 4-Card KpiCardGroup */}
      <div className="hidden md:block">
        <KpiCardGroup columns={4}>
          <KpiCard
            title={`Total ${currentConfig.name.split(' ')[0]}`}
            value={kpis.total.toString()}
            subtitle="Catalog entity records"
            icon={currentConfig.icon}
            variant="default"
          />
          <KpiCard
            title="Active Records"
            value={kpis.active.toString()}
            subtitle="Enabled for active operations"
            icon={CheckCircle2}
            variant="emerald"
          />
          <KpiCard
            title="Archived Records"
            value={kpis.archived.toString()}
            subtitle="Disabled / historical only"
            icon={XCircle}
            variant={kpis.archived > 0 ? 'amber' : 'default'}
          />
          <KpiCard
            title="Catalog Domain"
            value={currentConfig.table}
            subtitle="System relational entity"
            icon={Tag}
            variant="indigo"
          />
        </KpiCardGroup>
      </div>

      {/* 2. Mobile Compact Operational Summary Strip (< 768px, zero vertical clutter) */}
      <div className="md:hidden bg-slate-100/90 rounded-xl px-3.5 py-2.5 border border-slate-200/80 flex items-center justify-between text-xs font-semibold">
        <div className="flex items-center gap-2">
          <currentConfig.icon className="w-4 h-4 text-emerald-800 shrink-0" />
          <span className="text-slate-900 font-bold">{kpis.total} Total {currentConfig.itemNoun}s</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px]">
          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold">
            {kpis.active} Active
          </span>
          {kpis.archived > 0 && (
            <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full font-bold">
              {kpis.archived} Archived
            </span>
          )}
        </div>
      </div>

      {/* 3. Entity Selector & FilterBar */}
      <div className="space-y-3">
        {/* Desktop Horizontal Entity Tabs */}
        <div className="hidden md:flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar border-b border-slate-200">
          {ENTITY_CONFIGS.map((e) => {
            const Icon = e.icon;
            const isActive = activeEntity === e.id;
            return (
              <button
                key={e.id}
                onClick={() => setActiveEntity(e.id)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-t-lg text-xs font-bold transition-colors min-h-[44px] whitespace-nowrap border-b-2 ${
                  isActive
                    ? 'border-emerald-800 text-emerald-900 bg-emerald-50/70 shadow-2xs'
                    : 'border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{e.name}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile Obvious Entity Selector (Dropdown + Quick Swipe Chips) */}
        <div className="md:hidden space-y-2 bg-white p-3 border border-slate-200 rounded-xl shadow-xs">
          <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1">
            <Database className="w-3.5 h-3.5 text-emerald-700" />
            <span>Active Master Entity:</span>
          </label>
          <div className="relative">
            <select
              value={activeEntity}
              onChange={(e) => setActiveEntity(e.target.value as MasterEntityKey)}
              aria-label="Select Master Data Entity"
              className="w-full h-11 min-h-[44px] pl-3 pr-8 text-xs font-bold bg-slate-50 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-700 shadow-xs appearance-none"
            >
              {ENTITY_CONFIGS.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 text-xs">
              ▼
            </div>
          </div>

          {/* Quick 1-Tap Horizontal Entity Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar pt-1">
            {ENTITY_CONFIGS.map((e) => {
              const Icon = e.icon;
              const isActive = activeEntity === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => setActiveEntity(e.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all whitespace-nowrap min-h-[38px] ${
                    isActive
                      ? 'bg-emerald-800 text-white shadow-xs ring-1 ring-emerald-900/20'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span>{e.name.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* FilterBar with Search and Status Filter */}
        <FilterBar
          searchTerm={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={`Search ${currentConfig.name.toLowerCase()} by name, category...`}
          onReset={handleResetFilters}
          showReset={isFiltered}
        >
          <div className="w-full sm:w-44">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full h-10 min-h-[44px] rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="all">All Records</option>
              <option value="active">Active Only</option>
              <option value="archived">Archived Only</option>
            </select>
          </div>
        </FilterBar>
      </div>

      {/* 4. Desktop Table & Mobile Cards */}
      {loading ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <LoadingState message={`Loading ${currentConfig.name}...`} />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
          <EmptyState
            icon={currentConfig.icon}
            title={`No ${currentConfig.name} Found`}
            description="Try adjusting your search query or status filters."
          />
        </div>
      ) : (
        <div>
          {/* Desktop Table (md:table) */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-100/70 text-slate-700 font-semibold border-b border-slate-200 select-none">
                  <tr>
                    <th className="py-3 px-3.5 whitespace-nowrap">Primary Name / Label</th>
                    <th className="py-3 px-3.5 whitespace-nowrap">Category / Type</th>
                    <th className="py-3 px-3.5 whitespace-nowrap">Contact / Particulars</th>
                    <th className="py-3 px-3.5 text-center whitespace-nowrap">Status</th>
                    {canEdit && <th className="py-3 px-3.5 text-center whitespace-nowrap w-24">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white font-sans text-xs">
                  {filteredItems.map((item) => {
                    const isActive = item.is_active !== false;
                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 px-3.5 font-medium text-slate-900 whitespace-nowrap">
                          {item[currentConfig.nameField] || '—'}
                        </td>
                        <td className="py-3 px-3.5 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                          {item.category || item.supplier_type || item.rate_type || '—'}
                        </td>
                        <td className="py-3 px-3.5 text-slate-500 text-[11px] max-w-xs truncate">
                          {item.contact_number || item.contact_person || item.address || item.formula_expression || item.notes || '—'}
                        </td>
                        <td className="py-3 px-3.5 text-center whitespace-nowrap">
                          {isActive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                              <CheckCircle2 className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                              <XCircle className="w-3 h-3" /> Archived
                            </span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="py-3 px-3.5 text-center whitespace-nowrap">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(item)}
                                className="h-8 w-8 p-0 text-slate-600 hover:text-slate-900"
                                title="Edit Record"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleStatus(item)}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-amber-700"
                                title={isActive ? 'Archive Record' : 'Activate Record'}
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards (md:hidden) */}
          <div className="md:hidden space-y-3">
            {filteredItems.map((item) => {
              const isActive = item.is_active !== false;
              return (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-bold text-sm text-slate-900">
                        {item[currentConfig.nameField] || '—'}
                      </div>
                    </div>
                    {isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-600">
                        <XCircle className="w-3 h-3" /> Archived
                      </span>
                    )}
                  </div>

                  <div className="space-y-1 bg-slate-50 p-3 rounded-lg border border-slate-100 text-xs">
                    {(item.category || item.supplier_type || item.rate_type) && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Category / Type:</span>
                        <span className="font-mono font-medium text-slate-800">
                          {item.category || item.supplier_type || item.rate_type}
                        </span>
                      </div>
                    )}
                    {item.default_unit_price !== undefined && item.default_unit_price !== null && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Default Price:</span>
                        <span className="font-mono font-bold text-slate-900">
                          {formatCurrency(item.default_unit_price)}
                        </span>
                      </div>
                    )}
                    {(item.contact_number || item.contact_person) && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Contact:</span>
                        <span className="text-slate-700">
                          {[item.contact_person, item.contact_number].filter(Boolean).join(' • ')}
                        </span>
                      </div>
                    )}
                    {item.address && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Address:</span>
                        <span className="text-slate-700 truncate max-w-[200px]">{item.address}</span>
                      </div>
                    )}
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditModal(item)}
                        className="flex-1 h-11 min-h-[44px] text-xs font-semibold text-slate-700 border-slate-300 justify-center gap-1.5"
                      >
                        <Edit2 className="w-4 h-4 text-slate-600" />
                        <span>Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleStatus(item)}
                        className="h-11 min-h-[44px] px-4 text-xs font-semibold text-slate-500 hover:text-amber-700 border border-slate-200"
                        title={isActive ? 'Archive Record' : 'Activate Record'}
                      >
                        <Archive className="w-4 h-4" />
                        <span className="ml-1.5">{isActive ? 'Archive' : 'Activate'}</span>
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Record Modal */}
      {isCreateOpen && (
        <Dialog open={true} onOpenChange={() => setIsCreateOpen(false)}>
          <DialogContent className="max-w-lg p-6 bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader className="border-b border-slate-200 pb-3">
              <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-700" />
                <span>Add New {currentConfig.itemNoun}</span>
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleCreateSubmit} className="space-y-4 pt-3">
              {formError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-md">
                  {formError}
                </div>
              )}

              {renderFormFields()}

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Notes / Description</label>
                <textarea
                  rows={2}
                  placeholder="Optional internal remarks..."
                  value={formData.notes || ''}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <DialogFooter className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateOpen(false)}
                  className="min-h-[44px] text-xs font-semibold text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="min-h-[44px] bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold"
                >
                  {saving ? 'Saving...' : `Create ${currentConfig.itemNoun}`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Record Modal */}
      {isEditOpen && selectedItem && (
        <Dialog open={true} onOpenChange={() => setIsEditOpen(false)}>
          <DialogContent className="max-w-lg p-6 bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader className="border-b border-slate-200 pb-3">
              <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Edit2 className="w-4 h-4 text-slate-700" />
                <span>Edit {currentConfig.itemNoun}</span>
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleEditSubmit} className="space-y-4 pt-3">
              {formError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-md">
                  {formError}
                </div>
              )}

              {renderFormFields()}

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">Notes / Description</label>
                <textarea
                  rows={2}
                  placeholder="Optional internal remarks..."
                  value={formData.notes || ''}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              <DialogFooter className="mt-4 pt-3 border-t border-slate-200 flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditOpen(false)}
                  className="min-h-[44px] text-xs font-semibold text-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="min-h-[44px] bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-semibold"
                >
                  {saving ? 'Updating...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
